import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Shirt, 
  Sparkles, 
  PackageOpen, 
  ShoppingBag,
  MessageSquare,
  LogOut,
  Menu,
  X,
  User
} from 'lucide-react';
import { clientAPI } from '../services/api';

const ClientLayout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [clientName, setClientName] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  
  const clientId = localStorage.getItem('clientId');

  useEffect(() => {
    if (!clientId) {
      navigate('/cabinet/login');
      return;
    }
    
    // Fetch client profile
    const fetchProfile = async () => {
      try {
        const profile = await clientAPI.getProfile(clientId);
        setClientName(profile.name);
      } catch (err) {
        console.error('Error fetching profile:', err);
      }
    };
    fetchProfile();
  }, [clientId, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('clientId');
    localStorage.removeItem('clientToken');
    navigate('/cabinet/login');
  };

  const navItems = [
    { to: '/cabinet', icon: LayoutDashboard, label: 'Дашборд', exact: true },
    { to: '/cabinet/requests', icon: MessageSquare, label: 'Запросы' },
    { to: '/cabinet/wardrobe', icon: Shirt, label: 'Гардероб' },
    { to: '/cabinet/looks', icon: Sparkles, label: 'Образы' },
    { to: '/cabinet/capsules', icon: PackageOpen, label: 'Капсулы' },
    { to: '/cabinet/recommendations', icon: ShoppingBag, label: 'Покупки' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div style={{ 
      display: 'flex', 
      minHeight: '100vh',
      backgroundColor: 'var(--cream)'
    }}>
      {/* Sidebar - Desktop */}
      <aside style={{
        width: '260px',
        backgroundColor: 'var(--white)',
        borderRight: '1px solid var(--soft-gray)',
        display: 'none',
        flexDirection: 'column',
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: 100
      }}
      className="sidebar-desktop"
      >
        {/* Logo */}
        <div style={{
          padding: 'var(--space-xl)',
          borderBottom: '1px solid var(--soft-gray)'
        }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <span style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.25rem',
              color: 'var(--black)',
              fontWeight: 300
            }}>
              Наталья Ромашова
            </span>
          </Link>
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: 'var(--space-l) 0' }}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to, item.exact);
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-m)',
                  padding: 'var(--space-m) var(--space-xl)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: active ? 'var(--black)' : 'var(--mid-gray)',
                  textDecoration: 'none',
                  backgroundColor: active ? 'var(--cream)' : 'transparent',
                  borderLeft: active ? '3px solid var(--accent-gold)' : '3px solid transparent',
                  transition: 'all 200ms ease'
                }}
              >
                <Icon size={20} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div style={{
          padding: 'var(--space-l) var(--space-xl)',
          borderTop: '1px solid var(--soft-gray)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-m)',
            marginBottom: 'var(--space-m)'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'var(--cream)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <User size={20} style={{ color: 'var(--mid-gray)' }} />
            </div>
            <span style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              color: 'var(--charcoal)'
            }}>
              {clientName}
            </span>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-s)',
              background: 'none',
              border: 'none',
              fontFamily: 'var(--font-body)',
              fontSize: '0.8125rem',
              color: 'var(--mid-gray)',
              cursor: 'pointer',
              padding: 0
            }}
          >
            <LogOut size={16} />
            Выйти
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '60px',
        backgroundColor: 'var(--white)',
        borderBottom: '1px solid var(--soft-gray)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 var(--space-l)',
        zIndex: 200
      }}
      className="mobile-header"
      >
        <Link to="/" style={{ textDecoration: 'none' }}>
          <span style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.125rem',
            color: 'var(--black)'
          }}>
            Личный кабинет
          </span>
        </Link>
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 'var(--space-s)'
          }}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div style={{
          position: 'fixed',
          top: '60px',
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--white)',
          zIndex: 150,
          overflowY: 'auto'
        }}
        className="mobile-menu"
        >
          <nav style={{ padding: 'var(--space-m) 0' }}>
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.to, item.exact);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setIsMenuOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-m)',
                    padding: 'var(--space-l) var(--space-xl)',
                    fontFamily: 'var(--font-body)',
                    fontSize: '1rem',
                    color: active ? 'var(--black)' : 'var(--mid-gray)',
                    textDecoration: 'none',
                    backgroundColor: active ? 'var(--cream)' : 'transparent',
                    borderBottom: '1px solid var(--soft-gray)'
                  }}
                >
                  <Icon size={22} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div style={{ padding: 'var(--space-xl)' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-m)',
              marginBottom: 'var(--space-l)'
            }}>
              <User size={20} style={{ color: 'var(--mid-gray)' }} />
              <span style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                color: 'var(--charcoal)'
              }}>
                {clientName}
              </span>
            </div>
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-s)',
                background: 'none',
                border: 'none',
                fontFamily: 'var(--font-body)',
                fontSize: '1rem',
                color: 'var(--mid-gray)',
                cursor: 'pointer',
                padding: 0
              }}
            >
              <LogOut size={18} />
              Выйти
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main style={{
        flex: 1,
        marginLeft: 0,
        marginTop: '60px',
        padding: 'var(--space-l)',
        minHeight: 'calc(100vh - 60px)'
      }}
      className="main-content"
      >
        {children}
      </main>

      <style>{`
        @media (min-width: 769px) {
          .sidebar-desktop {
            display: flex !important;
          }
          .mobile-header {
            display: none !important;
          }
          .mobile-menu {
            display: none !important;
          }
          .main-content {
            margin-left: 260px !important;
            margin-top: 0 !important;
            padding: var(--space-xxl) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ClientLayout;
